import React, { useEffect } from "react";
import { useReactiveVar } from "@apollo/client";
import { isMenuShow, isPublishShow } from "../../graphql/state";
import { useLocation } from "react-router-dom";

const GlobalWidgets: React.FC = () => {
  const location = useLocation();

  const isShow = useReactiveVar(isMenuShow);
  const publishModal = useReactiveVar(isPublishShow);

  const ANALYTICS_ID = "6f065883-68cd-47a7-b674-93100a339ed1";
  const SCRIPT_SRC =
    "https://analytics.me-team.org/js/bundle.min.js?v=20250414174";

  useEffect(() => {
    const findWidget = () => {
      const iframe = document.querySelector(
        'iframe[name="helpcrunch-iframe"]'
      ) as HTMLElement;
      if (!iframe) return;
      iframe.style.bottom = isShow || publishModal.isActive ? "-100%" : "0px";
    };

    findWidget();
  }, [isShow, publishModal.isActive]);

  useEffect(() => {
    const isEditProjectPage = /^\/projects\/\d+\/edit$/.test(location.pathname);

    const interval = setInterval(() => {
      const iframe = document.querySelector(
        'iframe[name="helpcrunch-iframe"]'
      ) as HTMLElement | null;

      if (!iframe) return;

      iframe.style.position = "fixed";
      iframe.style.bottom = isEditProjectPage ? "100px" : "20px";
    }, 300);

    return () => clearInterval(interval);
  }, [location]);

  useEffect(() => {
    // Add HelpCrunch Chat widget
    window.helpcrunchSettings = {
      organization: "meteam",
      appId: "a49db63f-f5cd-4baa-b7dc-2691e28744c5",
    };
    const helpCrunchScript = document.createElement("script");
    helpCrunchScript.type = "text/javascript";
    helpCrunchScript.innerHTML = `
      (function(w,d){
        var hS = w.helpcrunchSettings;
        if (!hS || !hS.organization) {
          return;
        }
        var widgetSrc = 'https://embed.helpcrunch.com/sdk.js';
        w.HelpCrunch = function() {
          w.HelpCrunch.q.push(arguments);
        };
        w.HelpCrunch.q = [];
        
        function r() {
          if (d.querySelector('script[src="' + widgetSrc + '"]')) {
            return;
          }
          var s = d.createElement('script');
          s.async = 1;
          s.type = 'text/javascript';
          s.src = widgetSrc;
          (d.body || d.head).appendChild(s);
        }

        if (d.readyState === 'complete' || hS.loadImmediately) {
          r();
        } else if (w.attachEvent) {
          w.attachEvent('onload', r);
        } else {
          w.addEventListener('load', r, false);
        }
      })(window, document);
    `;
    document.body.appendChild(helpCrunchScript);

    // Hotjar script
    const hotjarScript = document.createElement("script");
    hotjarScript.innerHTML = `
      (function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:5060082,hjsv:6};
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `;
    document.head.appendChild(hotjarScript);

    // Google Analytics
    const googleAnalyticsScript = document.createElement("script");
    googleAnalyticsScript.async = true;
    googleAnalyticsScript.src =
      "https://www.googletagmanager.com/gtag/js?id=G-FEJPHSVMQV";
    document.head.appendChild(googleAnalyticsScript);

    const googleAnalyticsConfigScript = document.createElement("script");
    googleAnalyticsConfigScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-FEJPHSVMQV');
    `;
    document.head.appendChild(googleAnalyticsConfigScript);

    // LinkedIn Insight Tag
    const linkedinInitScript = document.createElement("script");
    linkedinInitScript.type = "text/javascript";
    linkedinInitScript.innerHTML = `
      var _linkedin_partner_id = "8426889";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    `;
    document.head.appendChild(linkedinInitScript);

    const linkedinMainScript = document.createElement("script");
    linkedinMainScript.type = "text/javascript";
    linkedinMainScript.innerHTML = `
      (function (l) {
        if (!l) {
          window.lintrk = function (a, b) {
            window.lintrk.q.push([a, b]);
          };
          window.lintrk.q = [];
        }
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";
        b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);
      })(window.lintrk);
    `;
    document.head.appendChild(linkedinMainScript);

    // LinkedIn noscript fallback (optional: inject if needed)
    const linkedinNoscript = document.createElement("noscript");
    linkedinNoscript.innerHTML = `
      <img height="1" width="1" style="display:none" alt=""
        src="https://px.ads.linkedin.com/collect/?pid=8426889&fmt=gif"/>
    `;
    document.body.appendChild(linkedinNoscript);

    const iframe = document.querySelector(
      'iframe[name="helpcrunch-iframe"]'
    ) as HTMLElement;
    if (iframe) iframe.style.zIndex = "999";

    return () => {
      document.body.removeChild(helpCrunchScript);
      document.head.removeChild(hotjarScript);
      document.head.removeChild(googleAnalyticsScript);
      document.head.removeChild(googleAnalyticsConfigScript);
      document.head.removeChild(linkedinInitScript);
      document.head.removeChild(linkedinMainScript);
      document.body.removeChild(linkedinNoscript);
    };
  }, []);

  useEffect(() => {
    if (!document.getElementById("mta-script")) {
      const script = document.createElement("script");
      script.id = "mta-script";
      script.defer = true;
      script.src = SCRIPT_SRC;
      script.onload = () => {
        if (typeof (window as any).initAnalytics === "function") {
          (window as any).initAnalytics(ANALYTICS_ID);
        }
      };
      document.body.appendChild(script);
    }

    const handler = () => {
      if (window.AnalyticsClient?.instance?.captureVisitEvent) {
        window.AnalyticsClient.instance.captureVisitEvent();
      }
    };
    window.addEventListener("mta_initialized", handler);

    return () => {
      window.removeEventListener("mta_initialized", handler);
    };
  }, []);

  return null;
};

export default GlobalWidgets;
